/**
 * Landing page sections — split from Landing.jsx so each piece is reusable,
 * testable, and stays under 200 lines.
 *
 * Sections exported:
 *   • LandingHeader (top nav)
 *   • LandingHero
 *   • StatBar
 *   • FeaturesGrid
 *   • LandingPricing
 *   • PoliceBanner
 *   • ComplianceSection
 *   • MeaningSection
 *   • TrustSection
 *   • TestimonialsPlaceholder
 *   • FinalCTA
 *   • LandingFooter
 *
 * Small leaf components (Stat, TrustBadge) live here too — they're only used
 * inside this file.
 */
import { Link } from "react-router-dom";
import {
  ScanLine, ShieldCheck, CalendarRange, ArrowRight, Mail, MessageSquareText,
  Building2, Camera, Lock, Server, FileCheck2, HardDriveDownload, History,
  Shield, MessageCircle, Check, Sparkles, BadgeEuro, Globe2, ScrollText, BookOpen,
} from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import FoxLogo from "@/components/FoxLogo";

const RECEPTION_IMG = "https://images.unsplash.com/photo-1533106497176-45ae19e68ba2?auto=format&fit=crop&w=1600&q=80";
const DETAILS_IMG = "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=1600&q=80";
const DEMO_MAILTO = "mailto:sales@xeniaregistry.com?subject=Richiesta%20demo%20Xenia";

export { DEMO_MAILTO };

/* ------------------ Header ------------------ */

export function LandingHeader({ t }) {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
          data-testid="brand-logo"
          aria-label="Xenia · torna alla home"
        >
          <div className="font-heading font-semibold text-2xl tracking-tight inline-flex items-center gap-2">
            <FoxLogo size={32} />Xenia<span className="text-blue-700">.</span>
          </div>
          <span className="hidden md:inline-block pl-3 ml-1 border-l border-slate-300 text-[10px] tracked text-blue-700 leading-tight max-w-[140px]">
            THE GLOBAL<br />GUEST REGISTRY
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm text-slate-700">
          <a href="#funzioni" className="hover:text-blue-700">{t("nav.functions")}</a>
          <a href="#prezzi" className="hover:text-blue-700 font-medium">{t("nav.pricing")}</a>
          <a href="#conformita" className="hover:text-blue-700">{t("nav.compliance")}</a>
          <Link to="/conformita" className="hover:text-blue-700 text-slate-500 text-xs underline-offset-2">{t("landing.full_page")}</Link>
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <Link to="/login" data-testid="nav-login" className="text-sm text-slate-700 hover:text-blue-700 px-2">{t("nav.login")}</Link>
          <Link to="/signup" data-testid="nav-demo" className="hidden sm:inline-flex text-sm text-blue-700 border-2 border-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50">{t("nav.demo")}</Link>
          <Link to="/signup" data-testid="nav-signup" className="text-sm bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
            {t("nav.signup")}
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ------------------ Hero ------------------ */

export function LandingHero({ t, tags }) {
  return (
    <section className="relative h-[92vh] min-h-[560px] w-full overflow-hidden bg-slate-950" data-testid="hero-video-section">
      {/* Background video — muted, autoplay, loop; falls back to poster if blocked */}
      <video
        src="/help/videos/scan.webm"
        poster="/help/screenshots/03-dashboard.png"
        autoPlay muted loop playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover opacity-70"
        aria-hidden="true"
      />
      {/* Dark corporate overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950/70 to-slate-950" />
      {/* Cinematic ambient dots */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />

      <div className="relative h-full max-w-6xl mx-auto px-6 flex flex-col justify-end pb-20 text-white">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur border border-white/20 rounded-full text-[11px] tracking-wide text-blue-100 mb-6 w-fit" data-testid="target-tags">
          <Sparkles className="w-3 h-3" /> {t("landing.tagline")}
        </div>
        <h1 className="font-heading font-semibold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.9] max-w-4xl">
          {t("landing.heading_1")}<br />
          {t("landing.heading_2")} <em className="italic text-blue-400">{t("landing.heading_3")}</em><br />
          {t("landing.heading_4")}
        </h1>
        <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed">{t("landing.subtitle")}</p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link to="/signup" data-testid="hero-cta-signup"
            className="group inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white px-7 py-4 rounded-lg text-base font-semibold shadow-[0_20px_40px_-10px_rgba(37,99,235,0.5)] transition-all">
            {t("landing.cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/signup" data-testid="hero-cta-demo"
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white border border-white/30 hover:bg-white/20 px-6 py-4 rounded-lg text-base font-medium transition-colors">
            {t("landing.demo_cta")}
          </Link>
        </div>
        <p className="mt-4 text-xs text-slate-400 inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> {t("landing.trial_no_card")}</p>
      </div>
    </section>
  );
}

/* ------------------ Stat bar ------------------ */

function Stat({ value, label, highlight }) {
  return (
    <div className={`bg-stone-900 px-6 py-8 lg:py-10 ${highlight ? "bg-gradient-to-br from-blue-700 to-blue-800" : ""}`} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight text-white leading-none mb-2">{value}</div>
      <div className="text-xs tracked text-slate-400 leading-snug">{label}</div>
    </div>
  );
}

export function StatBar({ t }) {
  return (
    <section className="bg-stone-900 text-stone-100 border-y border-stone-800" data-testid="stat-bar">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-800">
        <Stat value={t("landing.stat_uptime")} label={t("landing.stat_uptime_label")} />
        <Stat value={t("landing.stat_records")} label={t("landing.stat_records_label")} />
        <Stat value={t("landing.stat_languages")} label={t("landing.stat_languages_label")} />
        <Stat value={t("landing.stat_zero")} label={t("landing.stat_zero_label")} highlight />
      </div>
    </section>
  );
}

/* ------------------ Security Proof Band ------------------ */

export function SecurityProofBand({ t }) {
  const badges = [
    { label: t("landing.security.gdpr", "GDPR ready"), sub: t("landing.security.gdpr_sub", "Consenso versionato · export dati") },
    { label: t("landing.security.police", "Polizia · Alloggiati Web"), sub: t("landing.security.police_sub", "Certificato dal Ministero dell'Interno") },
    { label: t("landing.security.istat", "ISTAT C59"), sub: t("landing.security.istat_sub", "Report ufficiali generati in un click") },
    { label: t("landing.security.encryption", "Cifratura end-to-end"), sub: t("landing.security.encryption_sub", "TLS 1.3 · MongoDB EU · backup giornalieri") },
  ];
  return (
    <section className="bg-slate-900 text-slate-100 border-y border-slate-800" data-testid="security-proof-band">
      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-4 gap-6">
        {badges.map((b) => (
          <div key={b.label} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4 text-emerald-400" strokeWidth={2} />
            </div>
            <div>
              <div className="font-heading font-semibold text-white text-sm">{b.label}</div>
              <div className="text-xs text-slate-400 mt-0.5 leading-relaxed">{b.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------ Features grid ------------------ */

export function FeaturesGrid({ t }) {
  return (
    <section id="funzioni" className="py-24 border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <p className="text-xs tracked text-blue-700 mb-3"><span className="text-slate-400 mr-2">01 ·</span>{t("landing.functions_label")}</p>
          <h2 className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight max-w-2xl">{t("landing.functions_title")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
          <div className="md:col-span-4 bg-warm-amber border border-blue-200 p-8 min-h-[300px] rounded-lg">
            <div className="flex items-center gap-2 text-xs tracked text-blue-700 mb-4"><Camera className="w-4 h-4" />{t("landing.feature_camera_label")}</div>
            <h3 className="font-heading font-semibold text-3xl tracking-tight mb-3">{t("landing.feature_camera_title")}</h3>
            <p className="text-slate-700 max-w-md">{t("landing.feature_camera_desc")}</p>
          </div>
          <div className="md:col-span-2 bg-stone-900 text-stone-50 p-8 min-h-[300px] flex flex-col justify-between rounded-lg">
            <div>
              <div className="flex items-center gap-2 text-xs tracked text-sky-300 mb-4"><Building2 className="w-4 h-4" />{t("landing.feature_multi_label")}</div>
              <h3 className="font-heading font-semibold text-3xl tracking-tight">{t("landing.feature_multi_title")}</h3>
            </div>
            <p className="text-stone-300 text-sm">{t("landing.feature_multi_desc")}</p>
          </div>
          <div className="md:col-span-3 border-2 border-sage-200 bg-sage-50 p-8 min-h-[260px] rounded-lg">
            <div className="flex items-center gap-2 text-xs tracked text-sage-dark mb-4"><ShieldCheck className="w-4 h-4" />{t("landing.feature_police_label")}</div>
            <h3 className="font-heading font-semibold text-2xl tracking-tight mb-2">{t("landing.feature_police_title")}</h3>
            <p className="text-slate-700 text-sm">{t("landing.feature_police_desc")}</p>
          </div>
          <div className="md:col-span-3 border-2 border-sage-200 bg-sage-50 p-8 min-h-[260px] rounded-lg">
            <div className="flex items-center gap-2 text-xs tracked text-sage-dark mb-4"><CalendarRange className="w-4 h-4" />{t("landing.feature_tax_label")}</div>
            <h3 className="font-heading font-semibold text-2xl tracking-tight mb-2">{t("landing.feature_tax_title")}</h3>
            <p className="text-slate-700 text-sm">{t("landing.feature_tax_desc")}</p>
          </div>
          <div className="md:col-span-3 border border-slate-200 p-8 min-h-[220px] relative overflow-hidden bg-cover bg-center rounded-lg" style={{ backgroundImage: `url(${RECEPTION_IMG})` }}>
            <div className="absolute inset-0 bg-stone-950/60" />
            <div className="relative text-stone-50">
              <div className="flex items-center gap-2 text-xs tracked text-sky-300 mb-4"><Mail className="w-4 h-4" />{t("landing.feature_notif_label")}</div>
              <h3 className="font-heading font-semibold text-2xl tracking-tight mb-2">{t("landing.feature_notif_title")}</h3>
              <p className="text-stone-200 text-sm flex items-center gap-2"><MessageSquareText className="w-4 h-4" />{t("landing.feature_notif_desc")}</p>
            </div>
          </div>
          <div className="md:col-span-3 bg-blue-700 text-white p-8 min-h-[220px] flex items-center justify-between gap-6 rounded-lg">
            <div>
              <h3 className="font-heading font-semibold text-3xl tracking-tight">{t("landing.cta_title")}</h3>
              <p className="text-blue-50 mt-2 text-sm">{t("landing.cta_desc")}</p>
            </div>
            <Link to="/signup" data-testid="bento-cta-signup"
              className="inline-flex items-center gap-2 bg-white text-blue-700 px-5 py-3 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium whitespace-nowrap">
              {t("landing.cta_short")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------ Pricing ------------------ */

export function LandingPricing({ t, tags }) {
  return (
    <section id="prezzi" className="py-24 bg-warm-amber border-t border-blue-200">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs tracked text-blue-700 mb-3"><span className="text-slate-400 mr-2">02 ·</span>{t("landing.pricing_label")}</p>
          <h2 className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight mb-4">{t("landing.pricing_title")}</h2>
          <p className="text-slate-700 text-lg max-w-2xl mx-auto">{t("landing.pricing_subtitle")}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 mb-12">
          <div className="lg:col-span-5 bg-white border-2 border-blue-700 p-8 spotlight-ring rounded-lg" data-testid="pricing-main">
            <div className="inline-block text-xs tracked text-white bg-blue-700 px-3 py-1 mb-5">{t("landing.pricing_trial_badge")}</div>
            <div className="flex items-end gap-2 mb-1">
              <span className="font-heading font-semibold text-6xl text-stone-900">{t("landing.pricing_main_amount")}</span>
              <span className="text-slate-600 mb-2">{t("landing.pricing_main_period")}</span>
            </div>
            <p className="text-slate-700 text-sm mb-6">{t("landing.pricing_main_desc")}</p>
            <div className="border-t border-slate-200 pt-5 mt-5">
              <div className="flex items-end gap-2 mb-1">
                <span className="font-heading font-semibold text-3xl text-blue-700">{t("landing.pricing_usage_amount")}</span>
                <span className="text-slate-600 text-sm mb-1">{t("landing.pricing_usage_period")}</span>
              </div>
              <p className="text-slate-600 text-sm">{t("landing.pricing_usage_desc")}</p>
            </div>
            <p className="text-xs text-slate-600 mt-6 mb-3 inline-flex items-center gap-1.5">
              <Check className="w-3 h-3 text-sage-dark" /> {t("landing.pricing_trial_desc")}
            </p>
            <Link to="/signup" data-testid="pricing-cta-signup"
              className="block w-full text-center bg-blue-700 text-white px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors text-base font-medium mt-3">
              {t("landing.pricing_cta")}
            </Link>
            <Link to="/signup" data-testid="pricing-cta-demo"
              className="block w-full text-center text-blue-700 hover:underline mt-3 text-sm">
              {t("landing.pricing_demo")} →
            </Link>
          </div>

          <div className="lg:col-span-7 bg-white border border-slate-200 p-8 rounded-lg">
            <h3 className="font-heading font-semibold text-2xl tracking-tight mb-5 inline-flex items-center gap-2">
              <BadgeEuro className="w-5 h-5 text-blue-700" />{t("landing.pricing_included_title")}
            </h3>
            <ul className="grid sm:grid-cols-2 gap-3 mb-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <li key={`pi-${i}`} className="flex items-start gap-2 text-sm text-stone-800">
                  <Check className="w-4 h-4 text-sage-dark flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  {t(`landing.pricing_inc_${i}`)}
                </li>
              ))}
            </ul>
            <div className="border-t border-slate-200 pt-6">
              <p className="text-xs tracked text-slate-500 mb-4">{t("landing.why_title").toUpperCase()}</p>
              <ul className="space-y-3">
                {[1, 2, 3].map(i => (
                  <li key={`why-${i}`} className="flex items-start gap-3 text-sm text-stone-800">
                    <Check className="w-4 h-4 text-sage-dark flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{t(`landing.why_${i}`)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-sage-dark font-medium inline-flex items-center gap-1.5">
                <BadgeEuro className="w-4 h-4" /> {t("landing.why_savings")}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-blue-200 pt-10">
          <p className="text-xs tracked text-slate-600 mb-4">{t("landing.for_who_label")}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {tags.map(x => (
              <div key={x} className="border border-blue-200 bg-white/70 p-4 text-center hover:bg-blue-50 transition-colors rounded-lg">
                <div className="font-heading font-semibold text-xl tracking-tight">{x}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------ Police banner ------------------ */

export function PoliceBanner({ t }) {
  return (
    <section className="py-20 bg-stone-900 text-stone-50">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs tracked text-sky-300 mb-4"><span className="text-slate-500 mr-2">03 ·</span>{t("landing.police_banner_label")}</p>
          <h2 className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight">{t("landing.police_banner_title")}</h2>
          <p className="text-stone-300 mt-6 max-w-lg">{t("landing.police_banner_desc")}</p>
        </div>
        <img src={DETAILS_IMG} alt="" className="w-full h-[400px] object-cover rounded-lg" />
      </div>
    </section>
  );
}

/* ------------------ Compliance ------------------ */

export function ComplianceSection({ t }) {
  const items = [
    { icon: Shield, key: "gdpr" },
    { icon: ShieldCheck, key: "tulps" },
    { icon: BadgeEuro, key: "tax" },
    { icon: Lock, key: "privacy" },
    { icon: Globe2, key: "eidas" },
    { icon: ScrollText, key: "dpa" },
  ];
  return (
    <section id="conformita" className="py-24 bg-warm-sage border-t border-sage-200">
      <div className="max-w-7xl mx-auto px-6">
        <p className="text-xs tracked text-sage-dark mb-3"><span className="text-slate-400 mr-2">04 ·</span>{t("landing.compliance_label")}</p>
        <h2 className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight mb-3">{t("landing.compliance_title")}</h2>
        <p className="text-slate-700 max-w-2xl mb-12 text-lg">{t("landing.compliance_subtitle")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(({ icon: Icon, key }) => (
            <div key={key} data-testid={`comp-${key}`} className="bg-white border-2 border-sage-200 p-6 rounded-lg hover:border-sage-200 hover:shadow-md transition-all">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 bg-sage-100 border border-sage-200 flex items-center justify-center rounded-lg">
                  <Icon className="w-5 h-5 text-sage-dark" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-heading font-semibold text-xl tracking-tight">{t(`landing.comp_${key}_title`)}</div>
                  <div className="text-xs tracked text-slate-500 mt-1 mb-3">{t(`landing.comp_${key}_law`)}</div>
                  <p className="text-sm text-slate-700 leading-relaxed">{t(`landing.comp_${key}_desc`)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/conformita" data-testid="compliance-more"
            className="inline-flex items-center gap-2 bg-sage-100 text-sage-dark border-2 border-sage-200 px-6 py-3 rounded-lg hover:bg-white text-sm font-medium">
            {t("landing.compliance_more")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------ Brand meaning ------------------ */

export function MeaningSection({ t }) {
  return (
    <section id="significato" className="py-24 bg-slate-50 border-t border-slate-200" data-testid="meaning-section">
      <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5 text-center md:text-left">
          <p className="text-xs tracked text-blue-700 mb-4">{t("landing.name_label")}</p>
          <div className="font-heading font-semibold text-7xl lg:text-8xl text-stone-900 leading-none mb-3" lang="el">{t("landing.name_word")}</div>
          <div className="text-sm tracked text-slate-500 mb-1">{t("landing.name_translit")}</div>
          <div className="font-heading font-semibold italic text-2xl text-blue-700">«{t("landing.name_meaning")}»</div>
        </div>
        <div className="md:col-span-7 md:border-l md:border-slate-200 md:pl-10">
          <blockquote className="font-heading font-semibold text-xl lg:text-2xl tracking-tight text-stone-800 leading-snug mb-6">
            {t("landing.name_quote")}
          </blockquote>
          <p className="text-slate-700 leading-relaxed">{t("landing.name_today")}</p>
        </div>
      </div>
    </section>
  );
}

/* ------------------ Trust badges ------------------ */

function TrustBadge({ icon: Icon, title, desc, testid }) {
  return (
    <div className="bg-white p-6 hover:bg-blue-50/40 transition-colors" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 flex-shrink-0 border border-blue-200 bg-blue-50 flex items-center justify-center rounded-lg">
          <Icon className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-heading font-semibold text-lg tracking-tight mb-1">{title}</div>
          <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
        </div>
      </div>
    </div>
  );
}

export function TrustSection({ t }) {
  return (
    <section id="trust" className="py-24 bg-slate-50 border-t border-slate-200" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-6">
        <p className="text-xs tracked text-blue-700 mb-3"><span className="text-slate-400 mr-2">05 ·</span>{t("landing.trust_label")}</p>
        <h2 className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight max-w-3xl mb-3">{t("landing.trust_title")}</h2>
        <p className="text-slate-700 max-w-2xl mb-12">{t("landing.trust_subtitle")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-slate-200 rounded-lg overflow-hidden">
          <TrustBadge icon={Shield} title={t("landing.badge_gdpr_title")} desc={t("landing.badge_gdpr_desc")} testid="badge-gdpr" />
          <TrustBadge icon={Lock} title={t("landing.badge_ssl_title")} desc={t("landing.badge_ssl_desc")} testid="badge-ssl" />
          <TrustBadge icon={Server} title={t("landing.badge_eu_title")} desc={t("landing.badge_eu_desc")} testid="badge-eu" />
          <TrustBadge icon={FileCheck2} title={t("landing.badge_tulps_title")} desc={t("landing.badge_tulps_desc")} testid="badge-tulps" />
          <TrustBadge icon={HardDriveDownload} title={t("landing.badge_backup_title")} desc={t("landing.badge_backup_desc")} testid="badge-backup" />
          <TrustBadge icon={History} title={t("landing.badge_audit_title")} desc={t("landing.badge_audit_desc")} testid="badge-audit" />
        </div>
      </div>
    </section>
  );
}

/* ------------------ Testimonials placeholder ------------------ */

export function TestimonialsPlaceholder({ t }) {
  return (
    <section id="testimonials" className="py-24 bg-white border-t border-slate-200" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6">
        <p className="text-xs tracked text-blue-700 mb-3"><span className="text-slate-400 mr-2">06 ·</span>{t("landing.social_label")}</p>
        <h2 className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight max-w-3xl mb-3">{t("landing.social_title")}</h2>
        <p className="text-slate-600 max-w-2xl mb-12">{t("landing.social_subtitle")}</p>
        <div className="border-2 border-dashed border-blue-200 bg-warm-amber p-12 lg:p-16 text-center rounded-lg" data-testid="testimonials-placeholder">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white border-2 border-blue-200 mb-6">
            <MessageCircle className="w-7 h-7 text-blue-700" strokeWidth={1.5} />
          </div>
          <p className="text-xs tracked text-blue-700 mb-3">{t("landing.social_soon")}</p>
          <p className="text-stone-800 max-w-xl mx-auto mb-8 text-lg">{t("landing.social_cta")}</p>
          <Link to="/signup" data-testid="testimonials-cta-signup"
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium">
            {t("landing.social_cta_btn")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------ Final CTA + footer ------------------ */

export function FinalCTA({ t }) {
  return (
    <section className="py-20 bg-blue-700 text-white">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="font-heading font-semibold text-4xl lg:text-5xl tracking-tight mb-4">{t("landing.cta_title")}</h2>
        <p className="text-blue-50 text-lg mb-8 max-w-2xl mx-auto">{t("landing.cta_desc")}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/signup" data-testid="final-cta-signup"
            className="inline-flex items-center gap-2 bg-white text-blue-700 px-7 py-4 rounded-lg hover:bg-blue-50 text-base font-medium">
            {t("landing.cta")} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/signup" data-testid="final-cta-demo"
            className="inline-flex items-center gap-2 bg-blue-700 text-white border-2 border-blue-200 px-7 py-4 rounded-lg hover:bg-blue-800 text-base font-medium">
            {t("landing.demo_cta")}
          </Link>
        </div>
      </div>
    </section>
  );
}

export function LandingFooter({ t }) {
  return (
    <footer className="border-t border-slate-200 py-12 text-sm text-slate-600 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
        <div className="inline-flex items-center gap-3">
          <div className="font-heading font-semibold text-xl text-stone-900 inline-flex items-center gap-2"><FoxLogo size={28} />Xenia<span className="text-blue-700">.</span></div>
          <span className="hidden md:inline-block pl-3 border-l border-slate-300 text-[10px] tracked text-blue-700 leading-tight max-w-[140px]">
            THE GLOBAL<br />GUEST REGISTRY
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <Link to="/guida" data-testid="footer-guide" className="hover:text-blue-700 inline-flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> {t("landing.guide_link")}
          </Link>
          <Link to="/conformita" className="hover:text-blue-700">{t("landing.footer_compliance")}</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-blue-700">{t("landing.footer_privacy")}</Link>
          <Link to="/cookie-policy" className="hover:text-blue-700">{t("landing.footer_cookies")}</Link>
          <span>© {new Date().getFullYear()} Xenia</span>
        </div>
      </div>
    </footer>
  );
}
